import { apiFetch } from './client';
import type {
  Channel,
  ChannelCategory,
  ChannelMessagesResult,
  RawChannel,
  RawChannelMessagesResponse,
  RawPlayVoiceResponse,
  RawSubscription,
  RawVoiceMessage,
  VoiceMessage,
} from './types';

function mapCategory(category: ChannelCategory): ChannelCategory {
  return {
    ...category,
    price: Number(category.price || 0),
    messageCount: Number(category.message_count || category.messageCount || 0),
  };
}

function mapChannel(raw: RawChannel): Channel {
  const categories = (raw.categories || []).map(mapCategory);

  return {
    id: String(raw.id),
    name: raw.name || 'Untitled Channel',
    description: raw.description || '',
    category: raw.category || 'general',
    creatorName: raw.owner_nickname || 'Anonymous',
    creatorAvatar: raw.avatar_url || '',
    subscriberCount: Number(raw.subscriber_count || 0),
    messageCount: Number(raw.message_count || 0),
    totalPlays: Number(raw.total_plays || 0),
    subPrice: Number(raw.sub_price || 0),
    subscriptionStatus: raw.subscription?.status || raw.subscription_status || null,
    categories,
  };
}

function mapVoice(raw: RawVoiceMessage): VoiceMessage {
  return {
    id: String(raw.id),
    channelId: String(raw.channel_id),
    title: raw.title || 'Untitled message',
    description: raw.description || '',
    duration: Math.max(0, Math.round(Number(raw.duration_ms || 0) / 1000)),
    createdAt: raw.created_at,
    isLocked: Boolean(raw.locked),
    isPreview: raw.is_preview === 1,
    streamUrl: raw.voice_url,
    categoryId: raw.category_id ? String(raw.category_id) : null,
    categoryName: raw.category_name || null,
    categoryPrice: Number(raw.category_price || 0),
    playCount: Number(raw.play_count || 0),
  };
}

export async function fetchChannels(): Promise<Channel[]> {
  try {
    const raw = await apiFetch<{ items: RawChannel[] }>('/channels');
    return (raw.items || []).map(mapChannel);
  } catch {
    return [];
  }
}

export async function getChannel(channelId: string): Promise<Channel> {
  const raw = await apiFetch<RawChannel>(`/channels/${channelId}`);
  return mapChannel(raw);
}

export async function getChannelVoices(channelId: string, categoryId?: string): Promise<ChannelMessagesResult> {
  const query = categoryId ? `?category_id=${encodeURIComponent(categoryId)}` : '';
  const raw = await apiFetch<RawChannelMessagesResponse>(`/channels/${channelId}/messages${query}`);

  return {
    voices: raw.items.map(mapVoice),
    access: raw.access,
    pagination: raw.pagination,
  };
}

export async function playVoice(messageId: string): Promise<RawPlayVoiceResponse> {
  return apiFetch<RawPlayVoiceResponse>(`/channel/voice/${messageId}/play`, {
    method: 'POST',
  });
}

export function isSubscribed(subscription: RawSubscription['status'] | null | undefined): boolean {
  return subscription === 'active';
}
